import { Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, lighten } from '@mui/material';
import { FunnelContact } from 'features/co-pilot/co-pilot.types';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

const DataNotAvailable = () => {
  const { classes } = useStyles();
  return (
    <Stack direction={'row'} alignItems={'center'} gap={1}>
      <Typography className={classes.notAvailableText}>Data not available</Typography>
    </Stack>
  );
};

interface Props {
  contacts: FunnelContact[];
  selectedTestContacts: string[];
  finalContactsProcessing: boolean;

  handleSelectedTestContactChange(event: React.ChangeEvent<HTMLInputElement>): void;
}

const SelectContactsTable: React.FC<Props> = ({ contacts, selectedTestContacts, finalContactsProcessing, handleSelectedTestContactChange }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <TableContainer sx={{ height: '100%', width: '100%' }}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell style={{ width: 'calc(20px + 48px)' }} />
            <TableCell>{t('name')}</TableCell>
            <TableCell>{t('jobTitle')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {contacts.map((contact, index) => (
            <TableRow key={index}>
              <TableCell>
                <PaiCheckbox
                  checked={_.includes(selectedTestContacts, contact.linkedInURL)}
                  value={contact.linkedInURL}
                  dense={true}
                  onChange={handleSelectedTestContactChange}
                />
              </TableCell>
              <TableCell>
                {contact.fullName?.trim() ||
                  (!_.isNil(contact.firstName) && !_.isNil(contact.lastName) ? `${contact.firstName} ${contact.lastName}` : 'LinkedIn Contact')}
              </TableCell>
              <TableCell>{contact.occupation || <DataNotAvailable />}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {finalContactsProcessing && contacts.length < 5 ? (
        <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} gap={1} mt={2}>
          <PaiDotLoader size={'small'} inline={true} />
          <Typography className={classes.loadingText}>{t('processingContacts')}</Typography>
        </Stack>
      ) : null}
    </TableContainer>
  );
};

const useStyles = makeStyles()((theme) => ({
  selectContactsTable: {},
  table: {
    '& th': {
      border: 'none',
      padding: '4px 24px',
      fontSize: theme.typography.pxToRem(13),
      '&:first-of-type': {
        borderTopLeftRadius: '6px',
        borderBottomLeftRadius: '6px',
      },
      '&:last-of-type': {
        borderTopRightRadius: '6px',
        borderBottomRightRadius: '6px',
      },
    },
    '& td, th': {
      border: 'none',
      borderBottom: '1px solid #ddd',
      padding: '8px 24px',
      textAlign: 'left',
      verticalAlign: 'middle',
      fontSize: theme.typography.pxToRem(13),
      '& mark': {
        backgroundColor: lighten(theme.palette.secondary.main, 0.8),
      },
    },
  },
  notAvailableText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.light,
  },
  loadingText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.primary.main,
  },
}));

export default SelectContactsTable;
